import {forwardRef, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode} from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {cn} from '../lib/utils';
import {FieldLabel, FieldMessage} from '../lib/field';
import type {Testable} from '../lib/testable';
import {Button} from '../Button';
import {Stack} from '../Stack';
import {Typography} from '../Typography';
import {ChevronDownIcon, CloseIcon, SearchIcon, Success1Icon} from '../icons';

export type SelectProps = SelectSingleProps | SelectMultiProps;

const DEFAULT_MAX_CHIPS = 2;

export const Select = forwardRef<HTMLDivElement, SelectProps>((props, ref) => {
	const {
		options,
		label,
		error,
		hint,
		required = false,
		placeholder,
		searchable = false,
		disabled = false,
		id,
		testId,
		className
	} = props;

	const generatedId = useId();
	const fieldId = id ?? generatedId;
	const labelId = `${fieldId}-label`;
	const messageId = `${fieldId}-message`;
	const message = error ?? hint;
	const isMulti = props.multi === true;
	const maxChips = props.multi ? props.maxChips ?? DEFAULT_MAX_CHIPS : DEFAULT_MAX_CHIPS;
	const showBulkActions = props.multi ? props.showBulkActions ?? true : false;

	const [internalValue, setInternalValue] = useState(() => toValues(props.defaultValue));
	const [search, setSearch] = useState<string | null>(null);
	const [highlighted, setHighlighted] = useState(-1);
	const [open, setOpen] = useState(false);
	const listRef = useRef<HTMLDivElement>(null);
	const controlRef = useRef<HTMLDivElement>(null);
	const triggerSearchRef = useRef<HTMLInputElement>(null);
	const panelSearchRef = useRef<HTMLInputElement>(null);

	const setControlRef = (element: HTMLDivElement | null) => {
		controlRef.current = element;
		if (typeof ref === 'function') ref(element);
		else if (ref) (ref as {current: HTMLDivElement | null}).current = element;
	};

	const items = useMemo(() => options.map(normalize), [options]);
	const selected = 'value' in props ? toValues(props.value) : internalValue;
	const isSearching = search !== null;
	const query = (search ?? '').trim().toLowerCase();
	const visible = useMemo(
		() => query ? items.filter(item => optionText(item).toLowerCase().includes(query)) : items,
		[items, query]
	);

	const selectable = items.filter(item => !item.disabled);
	const allSelected = selectable.length > 0 && selectable.every(item => selected.includes(item.value));
	const selectedItems = selected.map(value => items.find(item => item.value === value) ?? {value});
	const optionId = (index: number) => `${fieldId}-option-${index}`;

	useEffect(() => {
		if (highlighted < 0) return;
		listRef.current?.querySelector<HTMLElement>('[data-highlighted]')?.scrollIntoView?.({block: 'nearest'});
	}, [highlighted]);

	const notify = props.onValueChange as unknown as ((value: string | string[] | undefined) => void) | undefined;

	const commit = (next: string[]) => {
		if (disabled) return;
		setInternalValue(next);
		notify?.(isMulti ? next : next[0]);
	};

	const changeOpen = (next: boolean) => {
		if (disabled) return;
		setOpen(next);
		setHighlighted(-1);
		setSearch(next && searchable && !isMulti ? '' : null);
	};

	const toggle = (option: SelectOption) => {
		if (option.disabled) return;
		if (isMulti) {
			commit(selected.includes(option.value)
				? selected.filter(value => value !== option.value)
				: [...selected, option.value]);
			return;
		}
		commit([option.value]);
		setSearch(null);
		setOpen(false);
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		if (disabled) return;

		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			event.stopPropagation();
			if (!open) return changeOpen(true);
			setHighlighted(current => nextEnabled(visible, current, event.key === 'ArrowDown' ? 1 : -1));
			return;
		}

		if (event.key === 'Enter') {
			if (!open) {
				event.preventDefault();
				return changeOpen(true);
			}
			event.preventDefault();
			event.stopPropagation();
			const option = visible[highlighted] ?? (visible.length === 1 ? visible[0] : undefined);
			if (option) toggle(option);
			return;
		}

		if (event.key === ' ' && !(event.target instanceof HTMLInputElement)) {
			event.preventDefault();
			changeOpen(!open);
		}
	};

	const border = disabled ? 'border-background-500'
		: error ? 'border-tone-red bg-background-700'
			: 'border-foreground-300 hover:border-foreground-200 data-[state=open]:border-foreground-200 ' +
				'focus-visible:border-foreground-200 has-[:focus-visible]:border-foreground-200';

	return (
		<div data-component='SelectField' className={cn(className, 'flex flex-col')}>
			{label &&
				<span id={labelId} className='mb-1.5 block w-fit'>
					<FieldLabel required={required} disabled={disabled}>{label}</FieldLabel>
				</span>}
			<PopoverPrimitive.Root open={open} onOpenChange={changeOpen} modal={false}>
				<PopoverPrimitive.Trigger asChild>
					<div
						ref={setControlRef}
						data-component='Select'
						data-testid={testId}
						role='combobox'
						aria-haspopup='listbox'
						aria-labelledby={label ? labelId : undefined}
						aria-required={required || undefined}
						aria-invalid={error ? true : undefined}
						aria-disabled={disabled || undefined}
						aria-describedby={message ? messageId : undefined}
						aria-activedescendant={open && highlighted >= 0 ? optionId(highlighted) : undefined}
						tabIndex={disabled || (searchable && !isMulti) ? undefined : 0}
						onKeyDown={handleKeyDown}
						className={cn(controlStyles, border)}>
						{isMulti
							? <Chips
								items={selectedItems}
								allSelected={allSelected}
								maxChips={maxChips}
								placeholder={placeholder}
								disabled={disabled}
								onRemove={value => commit(selected.filter(current => current !== value))}
								onClear={() => commit([])}/>
							: searchable
								? <input
									ref={triggerSearchRef}
									value={isSearching ? search : selectedItems[0] ? optionText(selectedItems[0]) : ''}
									placeholder={placeholder}
									autoComplete='off'
									disabled={disabled}
									onClick={event => {
										if (open) event.stopPropagation();
									}}
									onChange={event => {
										if (!open) changeOpen(true);
										setSearch(event.target.value);
										setHighlighted(-1);
									}}
									className={triggerInputStyles}/>
								: <SelectedText option={selectedItems[0]} placeholder={placeholder}/>}
						<ChevronDownIcon
							aria-hidden
							className={chevronStyles}/>
					</div>
				</PopoverPrimitive.Trigger>
				<PopoverPrimitive.Portal>
					<PopoverPrimitive.Content
						align='start'
						sideOffset={4}
						onKeyDown={handleKeyDown}
						onOpenAutoFocus={event => {
							event.preventDefault();
							(triggerSearchRef.current ?? panelSearchRef.current)?.focus();
						}}
						onCloseAutoFocus={event => {
							if (!triggerSearchRef.current) return;
							event.preventDefault();
							triggerSearchRef.current.focus();
						}}
						onInteractOutside={event => {
							if (controlRef.current?.contains(event.target as Node)) event.preventDefault();
						}}
						className={panelStyles}>
						{isMulti && searchable &&
							<div className={searchRowStyles}>
								<SearchIcon aria-hidden className='shrink-0 text-foreground-200'/>
								<input
									ref={panelSearchRef}
									value={search ?? ''}
									placeholder='Search'
									aria-label='Search'
									autoComplete='off'
									onChange={event => {
										setSearch(event.target.value);
										setHighlighted(-1);
									}}
									className={panelInputStyles}/>
							</div>}
						{showBulkActions && items.length > 0 &&
							<div className={bulkRowStyles}>
								<Button
									size='sm'
									variant='ghost'
									disabled={allSelected}
									onClick={() => commit(selectable.map(item => item.value))}
									className='px-2'>
									<Typography variant='tag2' color={allSelected ? 'textSecondary' : 'white'}>
										Select all
									</Typography>
									<Typography variant='tag2' color='textSecondary'>({selectable.length})</Typography>
								</Button>
								<Button
									size='sm'
									variant='ghost'
									disabled={selected.length === 0}
									onClick={() => commit([])}
									className='px-2'>
									<Typography variant='tag2' color={selected.length ? 'white' : 'textSecondary'}>
										Clear
									</Typography>
								</Button>
							</div>}
						<div
							ref={listRef}
							role='listbox'
							aria-multiselectable={isMulti || undefined}
							className='fade-scroll flex max-h-64 flex-col gap-1 overflow-y-auto'>
							{visible.length === 0
								? <div className='flex justify-center px-1 py-4'>
									<Typography variant='small2' color='textSecondary'>
										{items.length === 0 ? 'No options available' : 'No results found'}
									</Typography>
								</div>
								: visible.map((option, index) => (
									<SelectOptionRow
										key={option.value}
										id={optionId(index)}
										option={option}
										isSelected={selected.includes(option.value)}
										isHighlighted={highlighted === index}
										onSelect={() => toggle(option)}/>
								))}
						</div>
					</PopoverPrimitive.Content>
				</PopoverPrimitive.Portal>
			</PopoverPrimitive.Root>
			{message && <FieldMessage id={messageId} error={Boolean(error)}>{message}</FieldMessage>}
		</div>
	);
});

Select.displayName = 'Select';

const Placeholder = ({children}: {children?: ReactNode}) => (
	<Typography variant='small2' color='textMuted' noWrap className='min-w-0'>{children}</Typography>
);

const SelectedText = ({option, placeholder}: {option?: SelectOption; placeholder?: string}) => {
	if (!option) return <Placeholder>{placeholder}</Placeholder>;

	return (
		<Stack as='span' className='min-w-0'>
			{option.icon && <span className='flex shrink-0 [&_svg]:size-4'>{option.icon}</span>}
			<Typography variant='small2' color='textSecondary' noWrap className='min-w-0'>
				{option.label ?? option.value}
			</Typography>
		</Stack>
	);
};

const Chips = ({
	items, allSelected, maxChips, placeholder, disabled, onRemove, onClear
}: ChipsProps) => {
	if (items.length === 0) return <Placeholder>{placeholder}</Placeholder>;

	if (allSelected) return <Chip disabled={disabled} onRemove={onClear}>All selected</Chip>;

	const hidden = items.length - maxChips;

	return (
		<>
			{items.slice(0, maxChips).map(item => (
				<Chip key={item.value} disabled={disabled} onRemove={() => onRemove(item.value)}>
					{optionText(item)}
				</Chip>
			))}
			{hidden > 0 && <Chip>+{hidden}</Chip>}
		</>
	);
};

const Chip = ({children, disabled = false, onRemove}: ChipProps) => (
	<Stack
		as='span'
		gap={1}
		backgroundColor='background-500'
		borderRadius='sm'
		paddingY={0.5}
		paddingLeft={2}
		paddingRight={1}
		className='shrink-0'>
		<Typography variant='tag2' color='white' noWrap>{children}</Typography>
		{onRemove &&
			<button
				type='button'
				disabled={disabled}
				aria-label={`Remove ${typeof children === 'string' ? children : 'item'}`}
				onClick={event => {
					event.stopPropagation();
					onRemove();
				}}
				className='flex shrink-0 cursor-pointer text-foreground-white disabled:cursor-not-allowed'>
				<CloseIcon aria-hidden size='xs'/>
			</button>}
	</Stack>
);

const SelectOptionRow = ({
	id, option, isSelected, isHighlighted, onSelect
}: SelectOptionRowProps) => (
	<div
		id={id}
		role='option'
		aria-selected={isSelected}
		aria-disabled={option.disabled || undefined}
		data-highlighted={isHighlighted || undefined}
		onClick={onSelect}
		className={cn(
			optionStyles,
			option.helperText ? 'h-12 flex-col justify-center gap-0.5' : 'h-8 items-center',
			isSelected && 'bg-background-600',
			option.disabled && 'pointer-events-none'
		)}>
		<Stack as='span' gap={1.5} fullWidth className='min-w-0'>
			{option.icon && <span className='flex shrink-0 [&_svg]:size-4'>{option.icon}</span>}
			<Typography
				variant='small2'
				color={option.disabled ? 'textMuted' : isSelected ? 'white' : 'textSecondary'}
				noWrap
				className={cn('min-w-0', !option.disabled && activeTextStyles)}>
				{option.label ?? option.value}
			</Typography>
			<Success1Icon
				aria-hidden
				className={cn('ml-auto shrink-0 text-foreground-white', !isSelected && 'invisible')}/>
		</Stack>
		{option.helperText &&
			<Typography
				variant='small3'
				color='textSecondary'
				noWrap
				className={cn('min-w-0', option.icon && 'pl-[22px]')}>
				{option.helperText}
			</Typography>}
	</div>
);

const normalize = (option: string | SelectOption): SelectOption =>
	typeof option === 'string' ? {value: option} : option;

const optionText = (option: SelectOption) => typeof option.label === 'string' ? option.label : option.value;

const toValues = (value?: string | readonly string[]) =>
	value === undefined ? [] : typeof value === 'string' ? [value] : [...value];

const nextEnabled = (options: SelectOption[], from: number, delta: number) => {
	for (let index = from + delta; index >= 0 && index < options.length; index += delta)
		if (!options[index].disabled) return index;
	return delta < 0 ? -1 : from;
};

const controlStyles =
	'group relative flex min-h-8 cursor-pointer select-none flex-wrap items-center gap-1 ' +
	'rounded-md border py-1 pl-2 pr-7 text-foreground-200 transition-colors outline-none ' +
	'aria-disabled:cursor-not-allowed';

const chevronStyles =
	'absolute right-2 top-1/2 -translate-y-1/2 transition-transform duration-150 ' +
	'group-data-[state=open]:rotate-180';

const triggerInputStyles =
	'h-6 min-w-0 flex-1 cursor-text bg-transparent text-[12px] font-normal text-foreground-200 ' +
	'outline-none placeholder:text-foreground-300 disabled:cursor-not-allowed disabled:text-foreground-300';

const panelStyles =
	'v3 z-50 flex w-[var(--radix-popover-trigger-width)] min-w-60 flex-col items-stretch gap-1 ' +
	'rounded-md border border-background-600 bg-background-700 p-1 text-foreground-200 shadow-s';

const searchRowStyles =
	'flex h-8 shrink-0 items-center gap-1.5 rounded-t-md border-b border-background-500 pl-2 pr-4';

const bulkRowStyles =
	'flex shrink-0 items-center justify-between border-b border-background-500 pb-1';

const panelInputStyles =
	'min-w-0 flex-1 bg-transparent text-[12px] text-foreground-white outline-none placeholder:text-foreground-200';

const optionStyles =
	'group/option flex shrink-0 cursor-pointer select-none rounded-md border-b border-background-600 ' +
	'pl-2 pr-4 transition-colors last:border-b-0 hover:bg-background-600 ' +
	'data-[highlighted]:bg-background-600';

const activeTextStyles =
	'group-hover/option:text-foreground-white group-data-[highlighted]/option:text-foreground-white';

export interface SelectOption {
	value: string;
	label?: ReactNode;
	icon?: ReactNode;
	helperText?: ReactNode;
	disabled?: boolean;
}

interface SelectBaseProps extends Testable {
	options: readonly (string | SelectOption)[];
	label?: ReactNode;
	error?: ReactNode;
	hint?: ReactNode;
	required?: boolean;
	placeholder?: string;
	searchable?: boolean;
	disabled?: boolean;
	id?: string;
	className?: string;
}

interface SelectSingleProps extends SelectBaseProps {
	multi?: false;
	value?: string;
	defaultValue?: string;
	onValueChange?: (value: string | undefined) => void;
}

interface SelectMultiProps extends SelectBaseProps {
	multi: true;
	value?: readonly string[];
	defaultValue?: readonly string[];
	onValueChange?: (value: string[]) => void;
	maxChips?: number;
	showBulkActions?: boolean;
}

interface ChipsProps {
	items: SelectOption[];
	allSelected: boolean;
	maxChips: number;
	placeholder?: string;
	disabled?: boolean;
	onRemove: (value: string) => void;
	onClear: () => void;
}

interface ChipProps {
	children: ReactNode;
	disabled?: boolean;
	onRemove?: () => void;
}

interface SelectOptionRowProps {
	id: string;
	option: SelectOption;
	isSelected: boolean;
	isHighlighted: boolean;
	onSelect: () => void;
}
